/* eslint-disable no-console */
// Deep import on purpose: "@/3514/utils" is a hub barrel with import cycles, and this module is
// loaded during chat bootstrap where evaluation order matters.
import { addCoachingChatSocketBreadcrumb } from "@/3514/utils/sentry.helpers"

/**
 * Lifecycle diagnostics for the coaching chat.
 *
 * `[cm-ws]` (coaching-chat.api.socket.diagnostics.ts) already covers the transport: connects,
 * drops, reconnects, engine errors. What it cannot show is *why the UI decided what it decided* —
 * and every field incident so far has been a state decision, not a dropped connection:
 *
 *   - a finished chat re-initialised as "not started" (no summary, input enabled)
 *   - a socket opening for a chat that was already over
 *   - that socket never joining, so no history ever arrives and the feed renders empty
 *   - history arriving but being dropped, or appending a phantom "owl is typing" bubble
 *
 * All four are invisible in the network tab and in `[cm-ws]`. They are visible here.
 *
 * Search `[cm-state]` in the console or in Datadog/Sentry breadcrumbs, the same way `[cm-ws]` is
 * used today. Emitted at info level, so it is attached to any error report as a breadcrumb trail
 * without adding noise of its own.
 */
const LOG_PREFIX = "[cm-state]"

type TChatDiagnosticEvent =
    | "init" // mount-time hydration: what the chapter payload said and what state it produced
    | "socket-eligibility" // whether a socket may open for this chat, and which gate decided it
    | "join" // cm_join emitted, or skipped and why (no join => the server sends no history)
    | "history" // history received: size, last author, and whether it was applied
    | "chapter-invalidate" // chat completion invalidated ChapterById -> correlate with get_chapter_data in the network tab
    | "server-completed" // the socket answered with conversation_completed: the authoritative "this chat is over"

function logChatState(event: TChatDiagnosticEvent, data: Record<string, unknown>): void {
    const payload: Record<string, unknown> = { event, ts: new Date().toISOString(), ...data }

    console.info(`${LOG_PREFIX} ${event}`, payload)
    addCoachingChatSocketBreadcrumb(`${LOG_PREFIX} ${event}`, payload, "info")
}

export { logChatState as logCoachingChatState }
export type { TChatDiagnosticEvent as TCoachingChatDiagnosticEvent }
